import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Clock, CalendarOff, PencilLine, Save, X, Settings2, Trash2, AlertTriangle } from 'lucide-react';
import { attendanceAdminAPI, StaffShiftRow, ShiftScheduleItem } from '../../api/staffAttendanceManagement';
import { to12h } from './AttendanceEntryModal';

export const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const shiftLabel = (s: ShiftScheduleItem | null) =>
  s && s.shift_start && s.shift_end ? `${to12h(s.shift_start)} – ${to12h(s.shift_end)}` : null;

const shiftHours = (s: ShiftScheduleItem | null) => {
  if (!s || !s.shift_start || !s.shift_end) return null;
  const [ah, am] = s.shift_start.split(':').map(Number);
  const [bh, bm] = s.shift_end.split(':').map(Number);
  const mins = bh * 60 + bm - (ah * 60 + am);
  return `${Math.floor(mins / 60)}h${mins % 60 ? ` ${mins % 60}m` : ''}`;
};

export default function ShiftsWeekOffsTab({ searchQuery }: { searchQuery: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<StaffShiftRow | null>(null);
  const [form, setForm] = useState({ shift_start: '10:00', shift_end: '22:00', week_off_day: '' as string, effective_from: todayStr(), notes: '' });
  const [rulesForm, setRulesForm] = useState<{ grace: string; ot: string } | null>(null);

  const { data: shiftsRes, isLoading } = useQuery({ queryKey: ['attendanceShifts'], queryFn: attendanceAdminAPI.getShifts });
  const { data: rulesRes } = useQuery({ queryKey: ['attendanceRules'], queryFn: attendanceAdminAPI.getRules });
  const rows = (shiftsRes?.data || []).filter((r) =>
    !searchQuery || r.staff.full_name.toLowerCase().includes(searchQuery.toLowerCase()) || r.staff.staff_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const rules = rulesRes?.data;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['attendanceShifts'] });
    queryClient.invalidateQueries({ queryKey: ['staffAttendanceGrid'] });
    queryClient.invalidateQueries({ queryKey: ['staffSalarySummary'] });
  };

  const saveShift = useMutation({
    mutationFn: () => attendanceAdminAPI.setShift({
      staff_id: editing!.staff.id,
      shift_start: form.shift_start || null,
      shift_end: form.shift_end || null,
      week_off_day: form.week_off_day === '' ? null : Number(form.week_off_day),
      effective_from: form.effective_from,
      notes: form.notes || null,
    }),
    onSuccess: (res: any) => {
      if (res?.data?.salary_already_paid) toast('Saved. Salary for that month is already paid — recorded payment will not change.', { icon: '⚠️', duration: 6000 });
      else toast.success('Shift & week-off saved.');
      invalidateAll();
      setEditing(null);
    },
    onError: (err: any) => {
      const e = err?.response?.data?.error;
      toast.error(e?.details?.[0]?.message || e?.message || 'Failed to save shift.');
    },
  });

  const deleteShift = useMutation({
    mutationFn: (id: number) => attendanceAdminAPI.deleteShift(id),
    onSuccess: () => { toast.success('Schedule entry removed.'); invalidateAll(); },
    onError: () => toast.error('Failed to remove entry.'),
  });

  const saveRules = useMutation({
    mutationFn: () => attendanceAdminAPI.updateRules({ grace_minutes: Number(rulesForm!.grace), ot_min_minutes: Number(rulesForm!.ot) }),
    onSuccess: () => {
      toast.success('Attendance rules updated.');
      queryClient.invalidateQueries({ queryKey: ['attendanceRules'] });
      invalidateAll();
      setRulesForm(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.details?.[0]?.message || 'Failed to update rules.'),
  });

  const openEdit = (row: StaffShiftRow) => {
    const c = row.current;
    setForm({
      shift_start: c?.shift_start?.slice(0, 5) || '10:00',
      shift_end: c?.shift_end?.slice(0, 5) || '22:00',
      week_off_day: c && c.week_off_day !== null && c.week_off_day !== undefined ? String(c.week_off_day) : '',
      effective_from: todayStr(),
      notes: '',
    });
    setEditing(row);
  };

  const handleSave = () => {
    if (!form.shift_start || !form.shift_end) { toast.error('Shift start and end are required.'); return; }
    if (form.shift_end <= form.shift_start) { toast.error('Shift end must be after shift start (same day).'); return; }
    if (!form.effective_from) { toast.error('Effective-from date is required.'); return; }
    saveShift.mutate();
  };

  return (
    <div className="space-y-4">
      {/* Rules card */}
      <div className="p-3.5 bg-white border border-gray-300 rounded-md flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-2">
          <Settings2 className="w-4 h-4 text-red-600" />
          <div className="text-xs">
            <div className="font-black text-black">Attendance Rules (all staff)</div>
            <div className="font-semibold text-gray-700">
              Late grace: <b>{rules?.grace_minutes ?? 30} min</b> after shift start · Overtime paid only if <b>{rules?.ot_min_minutes ?? 60} min</b> or more after shift end (then full time, per minute)
            </div>
          </div>
        </div>
        {rulesForm ? (
          <div className="flex items-center gap-2 text-xs">
            <label className="font-bold">Grace
              <input id="rules-grace" type="number" min={0} value={rulesForm.grace} onChange={(e) => setRulesForm({ ...rulesForm, grace: e.target.value })}
                className="ml-1 w-16 px-2 py-1 border border-gray-300 rounded font-mono" /> min
            </label>
            <label className="font-bold">OT min
              <input id="rules-ot" type="number" min={0} value={rulesForm.ot} onChange={(e) => setRulesForm({ ...rulesForm, ot: e.target.value })}
                className="ml-1 w-16 px-2 py-1 border border-gray-300 rounded font-mono" /> min
            </label>
            <button id="rules-save" onClick={() => saveRules.mutate()} disabled={saveRules.isPending}
              className="px-3 py-1.5 bg-red-600 text-white rounded font-black cursor-pointer">Save</button>
            <button onClick={() => setRulesForm(null)} className="px-3 py-1.5 bg-white border border-gray-300 rounded font-black cursor-pointer">Cancel</button>
          </div>
        ) : (
          <button id="rules-edit" onClick={() => setRulesForm({ grace: String(rules?.grace_minutes ?? 30), ot: String(rules?.ot_min_minutes ?? 60) })}
            className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-100 rounded text-xs font-black cursor-pointer flex items-center space-x-1">
            <PencilLine className="w-3.5 h-3.5" /><span>Edit Rules</span>
          </button>
        )}
      </div>

      {/* Shifts table */}
      <div className="border border-gray-300 rounded-md bg-white overflow-hidden">
        {isLoading ? (
          <div className="text-center py-16 font-bold text-gray-600 animate-pulse text-sm">Loading shifts...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300 text-black font-black uppercase text-[10px] tracking-wider">
                  <th className="py-2.5 px-3">Staff</th>
                  <th className="py-2.5 px-2">Shift Time</th>
                  <th className="py-2.5 px-2 text-center">Hours</th>
                  <th className="py-2.5 px-2">Week-Off</th>
                  <th className="py-2.5 px-2">Effective From</th>
                  <th className="py-2.5 px-2">Upcoming Change</th>
                  <th className="py-2.5 px-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {rows.map((row) => {
                  const c = row.current;
                  const label = shiftLabel(c);
                  return (
                    <tr key={row.staff.id} className="hover:bg-gray-50">
                      <td className="py-2.5 px-3 font-black text-black">
                        <div>{row.staff.full_name}</div>
                        <div className="text-[10px] text-gray-500 font-mono">{row.staff.staff_code} • {row.staff.role} • {row.staff.salary_type}</div>
                      </td>
                      <td className="py-2.5 px-2 font-bold">
                        {label ? (
                          <span className="inline-flex items-center space-x-1 text-black"><Clock className="w-3.5 h-3.5 text-red-600" /><span>{label}</span></span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 rounded font-black text-[10px]">
                            <AlertTriangle className="w-3 h-3" /><span>Shift not set</span>
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-2 text-center font-mono font-bold">{shiftHours(c) || '—'}</td>
                      <td className="py-2.5 px-2 font-bold">
                        {c && c.week_off_day !== null && c.week_off_day !== undefined ? (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-purple-100 text-purple-900 border border-purple-300 rounded font-black text-[10px]">
                            <CalendarOff className="w-3 h-3" /><span>{WEEK_DAYS[c.week_off_day]}</span>
                          </span>
                        ) : <span className="text-gray-400">None</span>}
                      </td>
                      <td className="py-2.5 px-2 font-mono text-gray-700">{c ? String(c.effective_from).slice(0, 10) : '—'}</td>
                      <td className="py-2.5 px-2 text-[11px] font-semibold text-gray-700">
                        {row.upcoming.length === 0 ? '—' : row.upcoming.map((u) => (
                          <div key={u.id} className="flex items-center gap-1">
                            <span className="font-mono">{String(u.effective_from).slice(0, 10)}:</span>
                            <span>{shiftLabel(u) || 'no shift'}{u.week_off_day !== null ? `, off ${WEEK_DAYS[u.week_off_day].slice(0, 3)}` : ''}</span>
                            <button title="Remove" onClick={() => deleteShift.mutate(u.id)} className="text-rose-600 cursor-pointer"><Trash2 className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button id={`shift-edit-${row.staff.id}`} onClick={() => openEdit(row)}
                          className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white font-black text-xs rounded cursor-pointer">
                          {c ? 'Change' : 'Set Shift'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md border border-gray-300 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <span className="font-black text-sm text-black">Shift & Week-Off — {editing.staff.full_name}</span>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-200 rounded cursor-pointer"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-black text-gray-800 block mb-1">Shift Start *</span>
                  <input id="shift-start" type="time" value={form.shift_start} onChange={(e) => setForm({ ...form, shift_start: e.target.value })}
                    className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold" />
                  <span className="text-[11px] text-gray-600 font-bold">{to12h(form.shift_start)}</span>
                </label>
                <label className="block">
                  <span className="font-black text-gray-800 block mb-1">Shift End *</span>
                  <input id="shift-end" type="time" value={form.shift_end} onChange={(e) => setForm({ ...form, shift_end: e.target.value })}
                    className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold" />
                  <span className="text-[11px] text-gray-600 font-bold">{to12h(form.shift_end)}</span>
                </label>
              </div>
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">Week-Off Day</span>
                <select id="shift-weekoff" value={form.week_off_day} onChange={(e) => setForm({ ...form, week_off_day: e.target.value })}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold">
                  <option value="">No week-off</option>
                  {WEEK_DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">Effective From *</span>
                <input id="shift-effective" type="date" value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-bold" />
                <span className="text-[11px] text-gray-600 font-semibold">Applies from this date onward. Earlier days keep the old shift & week-off.</span>
              </label>
              <label className="block">
                <span className="font-black text-gray-800 block mb-1">Note</span>
                <input id="shift-notes" type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-2.5 py-2 border border-gray-300 rounded font-semibold" placeholder="Optional" />
              </label>

              {editing.history.length > 0 && (
                <div className="border border-gray-200 rounded">
                  <div className="px-2.5 py-1.5 bg-gray-50 border-b border-gray-200 font-black text-gray-800">History</div>
                  <div className="divide-y divide-gray-100 max-h-36 overflow-y-auto">
                    {editing.history.map((h) => (
                      <div key={h.id} className="px-2.5 py-1.5 text-[11px] font-semibold text-gray-700 flex justify-between">
                        <span><span className="font-mono">{String(h.effective_from).slice(0, 10)}</span> · {shiftLabel(h) || 'no shift'} · {h.week_off_day !== null ? `off ${WEEK_DAYS[h.week_off_day]}` : 'no week-off'}</span>
                        <span className="text-gray-500">{h.created_by_name || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50">
              <button onClick={() => setEditing(null)} className="px-3.5 py-2 bg-white border border-gray-300 rounded text-xs font-black cursor-pointer">Cancel</button>
              <button id="shift-save" onClick={handleSave} disabled={saveShift.isPending}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white rounded text-xs font-black flex items-center space-x-1.5 cursor-pointer">
                <Save className="w-3.5 h-3.5" /><span>{saveShift.isPending ? 'Saving...' : 'Save'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
